import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useActiveSmokingSession } from '@hooks/useActiveSmokingSession'
import {
  applyPauseToggle,
  elapsedSecondsFromStart,
  pausedDurationMs,
  type SmokingPauseState,
} from '@utils/smokingSessionState'

type SmokingSessionRuntimeContextValue = {
  activeSessionId: string | null
  paused: boolean
  setPaused: (paused: boolean) => void
  elapsedSeconds: number
  elapsedForSession: (sessionId: string, startTime?: string) => number
  isSessionPaused: (sessionId: string) => boolean
  setSessionPaused: (sessionId: string, paused: boolean) => void
}

const SmokingSessionRuntimeContext = createContext<SmokingSessionRuntimeContextValue | null>(null)

/** One shared 1s tick for all smoking timer UI (card + mini bar). */
const tickListeners = new Set<() => void>()
let tickInterval: ReturnType<typeof setInterval> | null = null

function ensureTickInterval() {
  if (tickInterval) return
  tickInterval = setInterval(() => {
    tickListeners.forEach((listener) => listener())
  }, 1000)
}

function releaseTickInterval() {
  if (tickListeners.size === 0 && tickInterval) {
    clearInterval(tickInterval)
    tickInterval = null
  }
}

function useSharedTick(enabled: boolean): number {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (!enabled) return
    const listener = () => setTick((t) => t + 1)
    tickListeners.add(listener)
    ensureTickInterval()
    return () => {
      tickListeners.delete(listener)
      releaseTickInterval()
    }
  }, [enabled])
  return tick
}

const idlePause: SmokingPauseState = { accumulatedMs: 0, pausedAt: null }

export function SmokingSessionRuntimeProvider({ children }: { children: ReactNode }) {
  const { activeSession } = useActiveSmokingSession()
  const pauseByIdRef = useRef<Map<string, SmokingPauseState>>(new Map())
  const prevActiveIdRef = useRef<string | null>(null)
  const [pauseGeneration, setPauseGeneration] = useState(0)
  const activeId = activeSession?.id ?? null
  const startTime = activeSession?.startTime

  useEffect(() => {
    const prev = prevActiveIdRef.current
    if (prev && prev !== activeId) {
      pauseByIdRef.current.delete(prev)
    }
    prevActiveIdRef.current = activeId
    if (activeId && !pauseByIdRef.current.has(activeId)) {
      pauseByIdRef.current.set(activeId, { ...idlePause })
    }
    if (!activeId) {
      pauseByIdRef.current.clear()
    }
    setPauseGeneration((g) => g + 1)
  }, [activeId])

  const tickEnabled = Boolean(activeId)
  const tick = useSharedTick(tickEnabled)
  const nowMs = useMemo(() => {
    void tick
    void pauseGeneration
    return Date.now()
  }, [tick, pauseGeneration])

  const elapsedForSession = useCallback(
    (sessionId: string, sessionStart?: string) =>
      elapsedSecondsFromStart(
        sessionStart,
        nowMs,
        pausedDurationMs(pauseByIdRef.current.get(sessionId), nowMs),
      ),
    [nowMs],
  )

  const isSessionPaused = useCallback(
    (sessionId: string) => {
      void pauseGeneration
      return pauseByIdRef.current.get(sessionId)?.pausedAt != null
    },
    [pauseGeneration],
  )

  const setSessionPaused = useCallback((sessionId: string, next: boolean) => {
    const now = Date.now()
    const current = pauseByIdRef.current.get(sessionId) ?? { ...idlePause }
    const updated = applyPauseToggle(current, next, now)
    if (updated === current) return
    pauseByIdRef.current.set(sessionId, updated)
    setPauseGeneration((g) => g + 1)
  }, [])

  const paused = activeId ? isSessionPaused(activeId) : false
  const elapsedSeconds = activeId ? elapsedForSession(activeId, startTime) : 0

  const setPaused = useCallback(
    (next: boolean) => {
      if (activeId) setSessionPaused(activeId, next)
    },
    [activeId, setSessionPaused],
  )

  const value = useMemo(
    () => ({
      activeSessionId: activeId,
      paused,
      setPaused,
      elapsedSeconds,
      elapsedForSession,
      isSessionPaused,
      setSessionPaused,
    }),
    [
      activeId,
      paused,
      setPaused,
      elapsedSeconds,
      elapsedForSession,
      isSessionPaused,
      setSessionPaused,
    ],
  )

  return (
    <SmokingSessionRuntimeContext.Provider value={value}>
      {children}
    </SmokingSessionRuntimeContext.Provider>
  )
}

const fallbackRuntime: SmokingSessionRuntimeContextValue = {
  activeSessionId: null,
  paused: false,
  setPaused: () => {},
  elapsedSeconds: 0,
  elapsedForSession: (_sessionId, startTime) => elapsedSecondsFromStart(startTime),
  isSessionPaused: () => false,
  setSessionPaused: () => {},
}

export function useSmokingSessionRuntime(): SmokingSessionRuntimeContextValue {
  const ctx = useContext(SmokingSessionRuntimeContext)
  return ctx ?? fallbackRuntime
}
